import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  UserPlus, Shield, Edit2, Trash2, Users, KeyRound, ToggleLeft, ToggleRight,
  CheckCircle2, XCircle, Search,
} from 'lucide-react';

const ROLE_LABELS = { administrator: 'Administrator', supervisor: 'Supervisor', scheduler: 'Scheduler', staff: 'Staff', employee: 'Staff' };
const ROLE_STYLES = {
  administrator: 'bg-[#2D1B4E] text-white',
  supervisor: 'bg-[#6B3FA0] text-white',
  scheduler: 'bg-[#9B7ED8] text-white',
  staff: 'bg-[#F0EDF6] text-[#5A4D6E]',
  employee: 'bg-[#F0EDF6] text-[#5A4D6E]',
};

function timeAgo(isoStr) {
  if (!isoStr) return 'Never';
  const d = new Date(isoStr);
  const diff = Math.floor((new Date() - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function UserRow({ u, currentUser, openEditAccount, openResetPassword, handleToggleActive, handleDeleteAccount, handleRoleChange }) {
  const isInactive = u.is_active === false;
  const isSelf = u.id === currentUser?.id;
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-200 hover:shadow-sm ${
        isInactive ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-[#DED8EB] hover:border-[#9B7ED8]'
      }`}
      data-testid={`account-${u.id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
          u.role === 'administrator' ? 'bg-[#2D1B4E] text-white' :
          u.role === 'supervisor' ? 'bg-[#6B3FA0] text-white' :
          u.role === 'scheduler' ? 'bg-[#9B7ED8] text-white' : 'bg-[#DED8EB] text-[#5A4D6E]'
        }`}>
          {u.name?.charAt(0) || '?'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-[#2D1B4E] truncate">{u.name}</p>
            {isSelf && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#6B3FA0]/10 text-[#6B3FA0] font-medium">You</span>}
            {isInactive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-medium">Inactive</span>}
          </div>
          <div className="flex items-center gap-2 text-xs text-[#8E85A0]">
            <span className="truncate">{u.email}</span>
            {u.last_login && <span className="hidden sm:inline">· {timeAgo(u.last_login)}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 ml-2">
        <Badge className={`text-[10px] ${ROLE_STYLES[u.role] || ROLE_STYLES.staff}`}>{ROLE_LABELS[u.role] || u.role}</Badge>
        {!isSelf && u.role !== 'administrator' && (
          <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)}
            className="text-xs border border-[#DED8EB] rounded-lg px-2 py-1.5 bg-white text-[#2D1B4E] hover:border-[#9B7ED8] transition-colors cursor-pointer" data-testid={`role-select-${u.id}`}>
            <option value="employee">Staff</option>
            <option value="scheduler">Scheduler</option>
            <option value="supervisor">Supervisor</option>
          </select>
        )}
        <Button variant="ghost" size="icon" onClick={() => openEditAccount(u)} className="h-8 w-8 text-[#8E85A0] hover:text-[#6B3FA0] hover:bg-[#F0EDF6]" data-testid={`edit-${u.id}`}>
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        {!isSelf && u.role !== 'administrator' && (
          <>
            <Button variant="ghost" size="icon" onClick={() => openResetPassword(u)} className="h-8 w-8 text-[#8E85A0] hover:text-[#FFB74D] hover:bg-orange-50" title="Reset password" data-testid={`reset-pw-${u.id}`}>
              <KeyRound className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => handleToggleActive(u.id, u.is_active !== false, u.name)}
              className={`h-8 w-8 ${isInactive ? 'text-green-500 hover:bg-green-50' : 'text-[#8E85A0] hover:text-amber-500 hover:bg-amber-50'}`}
              title={isInactive ? 'Activate' : 'Deactivate'} data-testid={`toggle-active-${u.id}`}>
              {isInactive ? <ToggleLeft className="h-3.5 w-3.5" /> : <ToggleRight className="h-3.5 w-3.5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => handleDeleteAccount(u.id, u.name)} className="h-8 w-8 text-[#8E85A0] hover:text-[#D9534F] hover:bg-red-50" data-testid={`delete-${u.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function RoleSection({ title, description, icon: Icon, users, color, rowProps }) {
  if (!users.length) return null;
  return (
    <div className="space-y-2" data-testid={`section-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2 px-1">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-sm font-semibold text-[#2D1B4E]">{title}</span>
        <span className="text-xs text-[#8E85A0]">({users.length})</span>
        <span className="text-xs text-[#8E85A0] hidden sm:inline">— {description}</span>
      </div>
      <div className="space-y-1.5">
        {users.map(u => <UserRow key={u.id} u={u} {...rowProps} />)}
      </div>
    </div>
  );
}

export default function TeamTab({
  allUsers, currentUser, searchQuery, setSearchQuery, openCreateAccount,
  openEditAccount, openResetPassword, handleToggleActive, handleDeleteAccount, handleRoleChange,
}) {
  const filteredUsers = allUsers.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });
  const sortAlpha = (arr) => [...arr].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const admins = sortAlpha(filteredUsers.filter(u => u.role === 'administrator'));
  const supervisors = sortAlpha(filteredUsers.filter(u => u.role === 'supervisor'));
  const schedulers = sortAlpha(filteredUsers.filter(u => u.role === 'scheduler'));
  const staff = sortAlpha(filteredUsers.filter(u => ['employee', 'staff'].includes(u.role)));
  const activeCount = allUsers.filter(u => u.is_active !== false).length;
  const inactiveCount = allUsers.filter(u => u.is_active === false).length;

  const rowProps = { currentUser, openEditAccount, openResetPassword, handleToggleActive, handleDeleteAccount, handleRoleChange };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm text-[#5A4D6E]">
          <Badge variant="outline" className="border-green-300 text-green-700 gap-1"><CheckCircle2 className="h-3 w-3" />{activeCount} active</Badge>
          {inactiveCount > 0 && <Badge variant="outline" className="border-red-300 text-red-600 gap-1"><XCircle className="h-3 w-3" />{inactiveCount} inactive</Badge>}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8E85A0]" />
            <Input placeholder="Search by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 border-[#DED8EB] bg-white h-9 text-sm" data-testid="search-accounts" />
          </div>
          <Button onClick={openCreateAccount} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2 h-9 text-sm shrink-0" data-testid="create-account-btn">
            <UserPlus className="h-4 w-4" /> Add
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        <RoleSection title="Administrators" description="Full system access" icon={Shield} users={admins} color="text-[#2D1B4E]" rowProps={rowProps} />
        <RoleSection title="Supervisors" description="Manage schedules & staff" icon={Shield} users={supervisors} color="text-[#6B3FA0]" rowProps={rowProps} />
        <RoleSection title="Schedulers" description="Edit schedules, import, print" icon={Users} users={schedulers} color="text-[#9B7ED8]" rowProps={rowProps} />
        <RoleSection title="Staff" description="View own schedule" icon={Users} users={staff} color="text-[#8E85A0]" rowProps={rowProps} />
      </div>
    </div>
  );
}
